/**
 * Booking lead time (DNKADM-0385, DNKAPI-0539) — the one place the console knows the
 * shape of `leadTimeMinutes`, shared by the tenant default (`tenantSettings.ts` /
 * `TenantSettingsCard`) and the per-service override (`services.ts` / `ServiceDialog`).
 *
 * The lead time is the minimum notice, in minutes, between "now" and a slot's start at
 * which a MEMBER may still book it. The front desk is never blocked by it. A service
 * override wins over the tenant default when set, including when it is shorter.
 *
 * Wire shape (both surfaces, the DNKADM-0374 billing-anchor sentinel):
 *  - GET: the stored minutes, or `null` when unset (never `0`);
 *  - PUT: `0` = clear (tenant → no lead time; service → inherit), `1..MAX` = set,
 *    omitted key = leave the stored value unchanged. Never write `null`.
 *
 * There is deliberately NO default number here: the shipped state is off, and every
 * real value is operator configuration (the spec forbids a placeholder/example value).
 */

/** The server's ceiling: 30 days in minutes (`LeadTime.MAX_MINUTES` in dink-api). */
export const LEAD_TIME_MAX_MINUTES = 30 * 24 * 60;

/**
 * Parse a wire `leadTimeMinutes` (GET): a whole number in `1..MAX` (truncated toward
 * zero if a numeric string) → that integer; anything else (absent, null, 0, out of
 * range, non-numeric) → `null` (unset). Mirrors `asBillingAnchorDay`.
 */
export function asLeadTimeMinutes(value: unknown): number | null {
  let n: number | null = null;
  if (typeof value === 'number' && Number.isFinite(value)) n = value;
  else if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) n = parsed;
  }
  if (n === null) return null;
  const t = Math.trunc(n);
  return t >= 1 && t <= LEAD_TIME_MAX_MINUTES ? t : null;
}

/** The units a lead time is entered in (DNKADM-0389 U4). Storage and the wire stay minutes. */
export type LeadTimeUnit = 'minutes' | 'hours' | 'days';
export const LEAD_TIME_UNITS: readonly LeadTimeUnit[] = ['minutes', 'hours', 'days'];
const UNIT_MINUTES: Record<LeadTimeUnit, number> = { minutes: 1, hours: 60, days: 1440 };

/** The selector label: `Minutes` / `Hours` / `Days`. */
export function leadTimeUnitTitle(unit: LeadTimeUnit): string {
  return unit === 'minutes' ? 'Minutes' : unit === 'hours' ? 'Hours' : 'Days';
}

/** The unit in words for a count: `1 hour`, `2 hours`, `90 minutes`, `1 day`. */
function unitWord(unit: LeadTimeUnit, count: number): string {
  const singular = unit === 'minutes' ? 'minute' : unit === 'hours' ? 'hour' : 'day';
  return count === 1 ? singular : `${singular}s`;
}

/** The server ceiling expressed in `unit` (43,200 minutes = 720 hours = 30 days). */
export function leadTimeMaxIn(unit: LeadTimeUnit): number {
  return LEAD_TIME_MAX_MINUTES / UNIT_MINUTES[unit];
}

/**
 * The stored minutes as the LARGEST unit that divides them evenly (DNKADM-0389 U4):
 * 120 → 2 hours, 90 → 90 minutes, 1440 → 1 day, 1500 → 25 hours.
 */
export function splitLeadTime(minutes: number): { amount: number; unit: LeadTimeUnit } {
  if (minutes > 0 && minutes % 1440 === 0) return { amount: minutes / 1440, unit: 'days' };
  if (minutes > 0 && minutes % 60 === 0) return { amount: minutes / 60, unit: 'hours' };
  return { amount: minutes, unit: 'minutes' };
}

/**
 * Validate a typed lead-time entry in `unit` and convert it to minutes (DNKADM-0389 U4).
 * Blank OR a typed `0` → valid, `null` — the spec's "blank or zero meaning no lead time"
 * (the caller sends the `0` sentinel or omits the key); a whole number from 1 up to the
 * server ceiling expressed in that unit → valid, the minutes; anything else → invalid, with
 * the message in the chosen unit. Rejects fractions, negatives, values past 30 days and text
 * inline — never hits the wire.
 */
export function parseLeadTimeEntry(
  raw: string,
  unit: LeadTimeUnit,
): { ok: true; value: number | null } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  const n = Number(trimmed);
  const max = leadTimeMaxIn(unit);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > max) {
    return { ok: false, message: leadTimeInvalidMessage(unit) };
  }
  return { ok: true, value: n === 0 ? null : n * UNIT_MINUTES[unit] };
}

/** The inline error for an invalid entry, in the chosen unit. */
export function leadTimeInvalidMessage(unit: LeadTimeUnit): string {
  const max = leadTimeMaxIn(unit).toLocaleString('en-US');
  const ceiling = unit === 'days' ? '' : ' (30 days)';
  return `Enter a whole number of ${unit} up to ${max}${ceiling}, or leave blank.`;
}

/**
 * What the setting does, in the operator's words (spec U3) — the same sentence on both
 * controls. The second half matters: without it the first support question is whether
 * the desk just lost the ability to take a walk-in.
 */
export const LEAD_TIME_BEHAVIOUR = 'Members cannot book within that lead time of the start time. The front desk still can.';

/**
 * Stored minutes in words, in the largest unit that divides them evenly (DNKADM-0389 U4):
 * `2 hours`, `90 minutes`, `1 day` — the read-only panel and the dialog's inherited line.
 */
export function leadTimeMinutesLabel(minutes: number): string {
  const { amount, unit } = splitLeadTime(minutes);
  return `${amount.toLocaleString('en-US')} ${unitWord(unit, amount)}`;
}
